/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("SplitterWidget", ["WidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Splitter widget.
   * @class SplitterWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   */
  cls.SplitterWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.SplitterWidget.prototype */ {
      __name: "SplitterWidget",

      _canHaveSplitter: false,
      _pagePosition: 0,
      _resizerDragPosition: 0,
      _isDragging: false,
      _splitInfo: null,
      _dragHandle: null,
      _forcedDefault: false,
      /** @type {classes.SplitterWidget} */
      _selectedSplitter: null,
      /**
       * the split holder
       * @type HTMLElement
       */
      _splitHolder: null,
      /**
       * True while a keyboard step is being applied — suppresses the deferred-mode drag visual.
       * @type {boolean}
       */
      _keyboardMoving: false,
      /**
       * Whether the primary pane is currently collapsed via the keyboard (Enter).
       * @type {boolean}
       */
      _collapsed: false,
      /**
       * Primary pane size (px) saved before a collapse, used to restore it on the next Enter.
       * @type {number}
       */
      _restorePrimaryPx: 0,

      _initLayout: function () {
        this._layoutInformation = new cls.LayoutInformation(this);
        this._layoutEngine = new cls.LeafLayoutEngine(this);
        this._layoutEngine._shouldFillHeight = true;
        this._layoutInformation.setMaximal(8, 8);
        this._layoutInformation.setMinimal(8, 8);
        this._layoutInformation.setMeasured(8, 8);
      },

      _initElement: function () {
        $super._initElement.call(this);
        this._dragHandle = this._element.getElementsByClassName(this.getBEMPartName("separator"))[0];
        this._element.setAttribute("draggable", "true");
        cls.DOMElementHelper.on(this._element, "dragstart.SplitterWidget", this._onDragStart.bind(this));
        cls.DOMElementHelper.on(this._element, "dragend.SplitterWidget", this._onDragEnd.bind(this));
        cls.DOMElementHelper.on(this._element, "drag.SplitterWidget", this._onDrag.bind(this));

        //handle touch events
        cls.DOMElementHelper.on(this._element, "touchstart.SplitterWidget", this._onTouchStart.bind(this));
        cls.DOMElementHelper.on(this._element, "touchend.SplitterWidget", this._onTouchEnd.bind(this));
        cls.DOMElementHelper.on(this._element, "touchmove.SplitterWidget", this._onTouchMove.bind(this));

        this._splitHolder = document.createElement("div");
        this._splitHolder.className = "gbc_SplitterWidget_splitholder";

        // Like any interactive widget, the splitter exposes its W3C window-splitter ARIA unconditionally
        // (GBC-5755). Whether Tab actually reaches it is decided by KeyboardService (accessibility mode),
        // not by the widget. Initial valuenow is populated after the first layout.
        this._updateAriaValues();
        this._scheduleAriaRefresh();
      },
      destroy: function () {
        cls.DOMElementHelper.off(this._element, "dragstart.SplitterWidget");
        cls.DOMElementHelper.off(this._element, "dragend.SplitterWidget");
        cls.DOMElementHelper.off(this._element, "drag.SplitterWidget");
        this._selectedSplitter = null;
        $super.destroy.call(this);
      },
      _onDragOver: function (evt) {
        cls.EventHelper.preventCancelableDefault(evt);
      },
      _onDragStart: function (evt) {
        if (!this._canHaveSplitter) {
          cls.EventHelper.preventCancelableDefault(evt);
        } else {
          this._selectedSplitter = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_SplitterWidget");
          this._selectedSplitter.addClasses("splitterMoving");
          this._element.parentNode.style.zIndex = 99999;
          this._splitHolder.style.zIndex = 99998;
          this._splitHolder.style.display = "block";
          cls.DOMElementHelper.on(this.getParentWidget()?.getElement(), "dragover.SplitterWidget", this._onDragOver.bind(this));
          cls.DOMElementHelper.on(this._splitHolder, "dragover.SplitterWidget", this._onDragOver.bind(this));
          this._isDragging = true;
          let image = new Image();
          image.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAQAAAAD+Fb1AAAADklEQVR42mNkgAJG3AwAAH4ABWjFc8IAAAAASUVORK5CYII=";

          if (window.browserInfo.isFirefox) {
            evt.dataTransfer.setData("text", ""); // for Firefox compatibility
            image = this._dragHandle;
          }

          if (evt.dataTransfer.setDragImage) {
            evt.dataTransfer.setDragImage(image, 0, -10000);
          }
          evt.dataTransfer.effectAllowed = "move";
          this._updateResizerDrag(evt);
          this.emit(context.constants.widgetEvents.splitterStart);
        }
        return false;
      },
      _updateResizerDrag: function (evt) {},
      _onDragEnd: function (evt) {
        this._selectedSplitter.removeClass("splitterMoving");
        this._selectedSplitter = null;
        this._element.style.zIndex = "";
        this._element.parentNode.style.zIndex = "";
        this._splitHolder.style.zIndex = "";
        this._splitHolder.style.display = "";
        cls.DOMElementHelper.off(this._splitHolder, "dragover.SplitterWidget");
        cls.DOMElementHelper.off(this.getParentWidget()?.getElement(), "dragover.SplitterWidget");
        this._isDragging = false;
        this.emit(context.constants.widgetEvents.splitterEnd);
        // A mouse resize invalidates a previous keyboard collapse; keep aria-valuenow in sync too
        // (silent: no focus, no announce).
        this._collapsed = false;
        this._scheduleAriaRefresh();
      },
      _onDrag: function (evt) {
        if (this._isDragging) {
          const delta = this._pagePosition - this._resizerDragPosition;
          this.updateSplits(delta);
        }
      },

      _onTouchStart: function (evt) {
        if (!this._canHaveSplitter) {
          cls.EventHelper.preventCancelableDefault(evt);
        } else {
          if (!this._selectedSplitter) {
            this._selectedSplitter = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_SplitterWidget");
            this._selectedSplitter.addClasses("splitterMoving");
          }
          this._element.parentNode.style.zIndex = 99999;
          this._splitHolder.style.zIndex = 99998;
          this._splitHolder.style.display = "block";
          this._isDragging = true;
          this._updateResizerDrag(evt);
          this.emit(context.constants.widgetEvents.splitterStart);
        }
        return false;
      },

      _onTouchEnd: function () {
        if (this._selectedSplitter) {
          this._selectedSplitter.removeClass("splitterMoving");
          this._selectedSplitter = null;
        }
        this._element.style.zIndex = "";
        this._element.parentNode.style.zIndex = "";
        this._splitHolder.style.zIndex = "";
        this._splitHolder.style.display = "";
        this._isDragging = false;
        this.emit(context.constants.widgetEvents.splitterEnd);
        this._collapsed = false;
        this._scheduleAriaRefresh();
      },

      _onTouchMove: function (evt) {
        if (this._isDragging) {
          const delta = this._pagePosition - this._splitStartPos;
          this.updateSplits(delta);
        }
      },

      _setDOMAttachedOrDetached: function () {
        if (this._element.parentNode) {
          this._element.parentNode.parentNode.appendChild(this._splitHolder);
        } else {
          this._splitHolder.remove();
        }
      },
      /**
       * Toggle the splitter state
       * @param {boolean} canSplit True to activate the splitter
       */
      activateSplitter: function (canSplit) {
        this._canHaveSplitter = Boolean(canSplit);
        this.toggleClass("canSplit", Boolean(canSplit));
        // Only an active splitter is a focus target; clear a stale tabindex when it becomes inert.
        if (this._canHaveSplitter) {
          this._updateDOMTabIndexFromState();
        } else if (this._element) {
          this._element.removeAttribute("tabindex");
        }
        this._updateAriaValues();
      },

      /**
       * The splitter root is the single focus target (one Tab stop), but only while it can actually
       * split. KeyboardService decides — per accessibility mode — whether the browser Tab order uses it.
       * @inheritDoc
       */
      getDOMTabIndexElements: function () {
        return this._canHaveSplitter && this._element ? [this._element] : [];
      },
      isReversed: function () {
        return this._parentWidget.isReversed();
      },
      updateSplits: function (delta) {
        if (!Number.isNaN(delta)) {
          this.emit(context.constants.widgetEvents.splitter, delta);
        }
      },

      // --- Accessibility: W3C Window Splitter keyboard control (GBC-5755). The splitter exposes its ARIA
      // unconditionally, like any interactive widget; KeyboardService decides — per accessibility mode —
      // whether Tab actually reaches it. ---

      /**
       * @returns {boolean} whether the splitter currently accepts a resize action
       */
      _isKeyboardOperable: function () {
        return this._canHaveSplitter && this.isVisible() && !this._isDragging;
      },

      /**
       * Handle arrow/Home/End/Enter as the focused splitter, ahead of 4GL accelerators (priority pipeline).
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this._isKeyboardOperable() && this._handleResizeKey(keyString)) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Apply a resize key for the current orientation. Arrow keys act only on the splitter's own axis
       * (Left/Right for a vertical bar, Up/Down for a horizontal one); Home/End/Enter always apply.
       * @param {string} keyString
       * @returns {boolean} true if the key was consumed
       */
      _handleResizeKey: function (keyString) {
        const horizontalBar = this._getAriaOrientation() === "horizontal"; // top/bottom panes -> Up/Down
        const decreaseKey = horizontalBar ? "up" : "left";
        const increaseKey = horizontalBar ? "down" : "right";
        if (keyString === decreaseKey) {
          this._moveByStep(-1);
        } else if (keyString === increaseKey) {
          this._moveByStep(1);
        } else if (keyString === "home") {
          this._moveToBound(-1);
        } else if (keyString === "end") {
          this._moveToBound(1);
        } else if (keyString === "enter" || keyString === "return") {
          // GBC renames the main Enter key to "return" (only NumpadEnter stays "enter") - handle both.
          this._toggleCollapse();
        } else {
          return false;
        }
        return true;
      },

      /**
       * Resize by one keyboard step (5% of the movable range). sign>0 grows the primary pane.
       * @param {number} sign +1 or -1
       */
      _moveByStep: function (sign) {
        const delta = sign * this._getStepDelta();
        if (delta) {
          this._performStep(this._screenDeltaForPrimaryGrow(delta));
        }
        this._collapsed = false;
        this._scheduleAriaRefresh();
      },

      /**
       * Jump the primary pane to its minimum (sign<0, Home) or maximum (sign>0, End); the engine clamps.
       * @param {number} sign +1 or -1
       */
      _moveToBound: function (sign) {
        const range = this._getFullRangePx();
        if (range) {
          this._performStep(this._screenDeltaForPrimaryGrow(sign * range));
        }
        this._collapsed = false;
        this._scheduleAriaRefresh();
      },

      /**
       * Enter: collapse the primary pane (remembering its size), or restore it to that size.
       */
      _toggleCollapse: function () {
        if (this._collapsed) {
          const delta = this._restorePrimaryPx - this._primarySizePx();
          if (delta) {
            this._performStep(this._screenDeltaForPrimaryGrow(delta));
          }
          this._collapsed = false;
        } else {
          this._restorePrimaryPx = this._primarySizePx();
          this._performStep(this._screenDeltaForPrimaryGrow(-this._getFullRangePx()));
          this._collapsed = true;
        }
        this._scheduleAriaRefresh();
      },

      /**
       * Write the ARIA value semantics (no-op unless exposed). valuenow/valuetext are recomputed from
       * the real size every call; the browser announces them on the focused separator (no aria-live needed).
       */
      _updateAriaValues: function () {
        if (!this._element) {
          return;
        }
        // aria-valuemin="0" / aria-valuemax="100" are constant — declared statically in the template.
        this.setAriaAttribute("orientation", this._getAriaOrientation());
        const label = this._getAriaLabel();
        this.setAriaAttribute("label", label);
        const controls = this._getAriaControlsId();
        this.setAriaAttribute("controls", controls || null);
        const now = this._computeValueNow();
        if (now === null || now === undefined) {
          return;
        }
        this.setAriaAttribute("valuenow", String(now));
        // "collapsed" is keyed on the explicit Enter-collapse state, not on valuenow===0: a pane at its
        // minimum (e.g. the sidebar's min width) is not "collapsed", and a keyboard collapse may stop at a
        // non-zero min size yet still be a collapse.
        // I18NService.t() takes no interpolation options — substitute placeholders manually (GBC convention).
        const valuetext = this._collapsed
          ? gbc.I18NService.t("gwc.splitter.collapsed").replace("{label}", label)
          : gbc.I18NService.t("gwc.splitter.position").replace("{label}", label).replace("{percentage}", String(now));
        this.setAriaAttribute("valuetext", valuetext);
      },

      /**
       * Refresh the ARIA values once the (asynchronous) relayout triggered by a resize has completed —
       * the allocated sizes `_computeValueNow` reads are only up to date after the layout pass.
       */
      _scheduleAriaRefresh: function () {
        const app = context.SessionService.getCurrent()?.getCurrentApplication?.();
        if (app?.layout?.afterLayout) {
          app.layout.afterLayout(this._updateAriaValues.bind(this), true);
        } else {
          this._updateAriaValues();
        }
      },

      /**
       * Apply one resize step. Default (box splitters): synthesize an atomic drag — reuses BoxWidget +
       * layout engine (min/max clamp, relayout, persistence). Overridden by SideBarSplitter.
       * @param {number} screenDelta - delta in drag/screen convention (updateSplits handles reversed)
       */
      _performStep: function (screenDelta) {
        this._keyboardMoving = true;
        this.emit(context.constants.widgetEvents.splitterStart);
        this.updateSplits(screenDelta);
        this.emit(context.constants.widgetEvents.splitterEnd);
        this._keyboardMoving = false;
      },

      /** @returns {string} localized accessible name for the splitter */
      _getAriaLabel: function () {
        return gbc.I18NService.t("gwc.splitter.label");
      },

      /** @returns {?string} id of the primary pane for aria-controls, or null to omit it */
      _getAriaControlsId: function () {
        return null;
      },

      // --- Per-subclass primitives (box vs sidebar) ---

      /** @returns {?number} current splitter position as a 0-100 percentage, or null if not computable */
      _computeValueNow: function () {
        return null;
      },

      /** @returns {number} keyboard step size in px (5% of the movable range) */
      _getStepDelta: function () {
        return 0;
      },

      /** @returns {number} full movable range in px (used for Home/End and collapse) */
      _getFullRangePx: function () {
        return 0;
      },

      /** @returns {number} current primary pane size in px */
      _primarySizePx: function () {
        return 0;
      },

      /** @returns {string} "vertical" (left/right bar) or "horizontal" (top/bottom bar) */
      _getAriaOrientation: function () {
        return "vertical";
      },

      /**
       * Convert a "grow primary pane by N px" delta into the drag/screen-convention delta expected by
       * `_performStep`/`updateSplits`. Default: identity. Overridden where reversed/orientation flips apply.
       * @param {number} primaryDelta
       * @returns {number}
       */
      _screenDeltaForPrimaryGrow: function (primaryDelta) {
        return primaryDelta;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Splitter", cls.SplitterWidget);
});
